import { ZONE_TYPES } from '../Helpers/constants';

// ============================================================
// MAP1 — Vallée de dana, 14 columns × 7 rows
//
// Region templates (name + difficulty + monster pool + zoneType) are
// applied to every cell labelled with the matching key in MAP1_LAYOUT.
// fightThreshold semantics: a random roll [0–100] greater than the
// threshold triggers combat, so LOWER threshold = MORE fights.
// ============================================================

const REGIONS = {
    sombrilForest: {
        name: "la forêt du Sombril",
        zoneType: ZONE_TYPES.FOREST,
        safeZone: false,
        fightThreshold: 30,                    // dense, dangerous
        monsterIdList: ["monster5"],
    },
    riquewihrForest: {
        name: "la forêt de Riquewihr",
        zoneType: ZONE_TYPES.FOREST,
        safeZone: false,
        fightThreshold: 50,                    // light forest
        monsterIdList: ["monster3", "monster4"],
    },
    foxRiversPlain: {
        name: "la plaine de Fox Rivers",
        zoneType: ZONE_TYPES.PLAIN,
        safeZone: false,
        fightThreshold: 70,                    // open road, low threat
        monsterIdList: ["monster1", "monster2"],
    },
    lysPlain: {
        name: "la plaine des lys",
        zoneType: ZONE_TYPES.PLAIN,
        safeZone: false,
        fightThreshold: 65,
        monsterIdList: ["monster1"],
    },
    mulhouseJungle: {
        name: "la jungle de Mulhouse",
        zoneType: ZONE_TYPES.FOREST,
        safeZone: false,
        fightThreshold: 20,                    // most dangerous wild zone
        monsterIdList: ["monster1", "monster2", "monster4"],
    },
    poudlardLake: {
        name: "le lac de poudlard",
        zoneType: ZONE_TYPES.WATER,
        safeZone: false,
        fightThreshold: 100,                   // no combat on water
        monsterIdList: [],
    },
    connemaraLake: {
        name: "le lac du connemara",
        zoneType: ZONE_TYPES.WATER,
        safeZone: false,
        fightThreshold: 100,
        monsterIdList: [],
    },
    godricCity: {
        name: "Godric's hollow",
        zoneType: ZONE_TYPES.CITY,
        safeZone: true,
        fightThreshold: 0,
        monsterIdList: [],
    },
};

// Visual layout (y = 0 is top row). Forests occupy NW + SW corners,
// lakes the NE + SE corners, plains cover the middle, and the city
// anchors at (5, 3). Total: 98 zones = 14 cols × 7 rows.
// Eastern strip (x=12, 13) extends each row's eastern region — Poudlard
// lake reaches further east at the top, lys plains widen mid-map,
// Connemara lake widens at the bottom.
const MAP1_LAYOUT = [
    /* y=0 */ ['sombrilForest','sombrilForest','sombrilForest','sombrilForest','riquewihrForest','riquewihrForest','riquewihrForest','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake'],
    /* y=1 */ ['sombrilForest','sombrilForest','sombrilForest','sombrilForest','riquewihrForest','riquewihrForest','riquewihrForest','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake','poudlardLake'],
    /* y=2 */ ['sombrilForest','sombrilForest','sombrilForest','foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain'],
    /* y=3 */ ['foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','godricCity','foxRiversPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain'],
    /* y=4 */ ['foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','foxRiversPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain','lysPlain'],
    /* y=5 */ ['mulhouseJungle','mulhouseJungle','mulhouseJungle','mulhouseJungle','mulhouseJungle','foxRiversPlain','lysPlain','lysPlain','lysPlain','connemaraLake','connemaraLake','connemaraLake','connemaraLake','connemaraLake'],
    /* y=6 */ ['mulhouseJungle','mulhouseJungle','mulhouseJungle','mulhouseJungle','mulhouseJungle','foxRiversPlain','lysPlain','lysPlain','lysPlain','connemaraLake','connemaraLake','connemaraLake','connemaraLake','connemaraLake'],
];

const buildZones = (layout) => {
    const zones = {};
    layout.forEach((row, y) => {
        row.forEach((regionKey, x) => {
            const region = REGIONS[regionKey];
            if (!region) return;
            const id = `map${x}${y}`;
            zones[id] = { id, x, y, ...region };
        });
    });
    return zones;
};

export const MAPS = {
    'map1': {
        id: 'map1',
        name: 'Vallée de dana',
        columns: 14,
        rows: 7,
        zones: buildZones(MAP1_LAYOUT),
    },
};

const TILES_SPRITESHEET = '/assets/images/tiles-spritesheet.png';

// Natural dimensions of the spritesheet PNG. Used to compute background-size
// so each tile rect lands precisely inside its grid cell. Update if the
// spritesheet asset changes (cf. T10 étape 2 — okoyl-tiles-spritesheet.png).
export const TILES_SPRITESHEET_DIMS = { width: 1408, height: 1104 };

// Each grid cell stores the zoneType string of its zone. Map.js looks up
// TILES_DATA[zoneType] to render the correct sprite.
const buildGridFromZones = (mapDef) => {
    const grid = [];
    const zones = Object.values(mapDef.zones || {});
    for (let y = 0; y < mapDef.rows; y++) {
        const row = [];
        for (let x = 0; x < mapDef.columns; x++) {
            const zone = zones.find(z => z.x === x && z.y === y);
            row.push(zone ? zone.zoneType : null);
        }
        grid.push(row);
    }
    return grid;
};

Object.values(MAPS).forEach(mapDef => {
    if (!mapDef.grid && mapDef.zones) {
        mapDef.grid = buildGridFromZones(mapDef);
    }
});

export const TILES_DATA = {
    [ZONE_TYPES.FOREST]: {
        texture: TILES_SPRITESHEET,
        rectX: -80,
        rectY: -16,
        rectWidth: 16,
        rectHeight: 16,
        baseScale: 4,
    },
    [ZONE_TYPES.WATER]: {
        texture: TILES_SPRITESHEET,
        rectX: -608,
        rectY: 0,
        rectWidth: 16,
        rectHeight: 16,
        baseScale: 4,
    },
    [ZONE_TYPES.PLAIN]: {
        texture: TILES_SPRITESHEET,
        rectX: -16,
        rectY: 0,
        rectWidth: 16,
        rectHeight: 16,
        baseScale: 4,
    },
    [ZONE_TYPES.CITY]: {
        texture: TILES_SPRITESHEET,
        rectX: 0,
        rectY: -144,
        rectWidth: 64,
        rectHeight: 64,
        baseScale: 1,
    }
}

const SHOPKEEPER_DIALOGUES = {
    NORMAL: {
        welcome: "Bienvenue dans ma boutique !",
        notEnoughGold: "Vous n'avez pas assez de gauld, revenez plus tard!",
        inventoryFull: "Tu n'as plus de place dans ton sac à dos, vide le un peu",
        saleSuccess: "Merci pour ton achat",
        goodBye: "Au revoir !"

    },
}

// Keyed by zone id (matches the city zone in MAPS.map1.zones).
// Godric's Hollow now sits at x=5, y=3 → 'map53'.
export const CITY_DATA = {
    'map53': {
        type: "desert-city",
        shops: {
            shop1: {
                name: "La boutique de Lulu",
                shopkeeperData: {
                    portrait: "assets/images/png/shopkeeper.png",
                    dialogues: SHOPKEEPER_DIALOGUES.NORMAL
                },
                description: "ici vous pouvez acheter des bâtons",
                icon: "🛍️",
                items: ['potionLittleHP', 'potionSmallHP', 'potionLittleMP', 'potionSmallMP', 'potionSmallElixir', 'potionBigElixir']
            },
            shop2: {
                name: "La boutique de Michelle Jaqueson",
                shopkeeperData: {
                    portrait: "assets/images/png/shopkeeper.png",
                    dialogues: SHOPKEEPER_DIALOGUES.NORMAL
                },
                description: "ici vous pouvez acheter des bâtons",
                icon: "🕌",
                items: ['potionLittleHP', 'potionSmallHP', 'potionLittleMP', 'potionSmallMP', 'potionSmallElixir', 'potionBigElixir']
            }
        }
    }
}